import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { APPEARANCE_PREVIEW_QUERY } from '@/theme/tokens';
import { SLIDESHOW_VIEW_QUERY, normalizeExperienceOverride, resolveExperience } from '@/slideshow/experience';

function overrideFromFrameUrl() {
    if (typeof window === 'undefined') return null;
    const query = new URLSearchParams(window.location.search);
    if (query.get(APPEARANCE_PREVIEW_QUERY) !== '1') return null;
    return normalizeExperienceOverride({ view: query.get(SLIDESHOW_VIEW_QUERY) });
}

// Set by the Appearance preview bridge; always null on the live site.
let override = overrideFromFrameUrl();
const listeners = new Set();

export function setSlideshowExperienceOverride(value) {
    const next = normalizeExperienceOverride(value);
    if (JSON.stringify(next) === JSON.stringify(override)) return;
    override = next;
    listeners.forEach((listener) => listener());
}

export function getSlideshowExperienceOverride() {
    return override;
}

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

function readTouch() {
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
    return hasTouch && window.matchMedia('(pointer: coarse)').matches;
}

function readDevice() {
    return {
        width: window.innerWidth,
        height: window.innerHeight,
        screenMin: Math.min(window.screen.width, window.screen.height),
        touch: readTouch(),
    };
}

/**
 * The slideshow's view of the device, see lib/slideshow/experience.js.
 * Server render and the first client render measure nothing (a small,
 * non-touch, unmeasured screen) so hydration matches; `detected` turns
 * true once the real device has been read.
 */
export function useSlideshowExperience() {
    const forced = useSyncExternalStore(subscribe, getSlideshowExperienceOverride, () => null);
    const [device, setDevice] = useState(null);

    useEffect(() => {
        const update = () => {
            setDevice((prev) => {
                const next = readDevice();
                if (prev
                    && prev.width === next.width
                    && prev.height === next.height
                    && prev.screenMin === next.screenMin
                    && prev.touch === next.touch) return prev;
                return next;
            });
        };
        update();
        const pointerQuery = window.matchMedia('(pointer: coarse)');
        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', update);
        pointerQuery.addEventListener?.('change', update);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', update);
            pointerQuery.removeEventListener?.('change', update);
        };
    }, []);

    return useMemo(() => ({
        ...resolveExperience({ ...(device || {}), override: device ? forced : null }),
        detected: Boolean(device),
    }), [device, forced]);
}

export default useSlideshowExperience;
